import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import Button from '../Button/Button';
import Input from '../Input/Input';
import DeleteButton from '../DeleteButton/DeleteButton';
import './EditKeyDialog.css';

interface EditKeyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newKeyName: string) => Promise<void>;
  onDelete: () => Promise<void>;
  keyName: string;
  isLoading?: boolean;
}

const EditKeyDialog: React.FC<EditKeyDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  keyName,
  isLoading = false
}) => {
  const [newKeyName, setNewKeyName] = useState(keyName);
  const [error, setError] = useState<string | undefined>();
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setNewKeyName(keyName);
      setError(undefined);
      setIsDeleting(false);
    }
  }, [isOpen, keyName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate
    if (!newKeyName.trim()) {
      setError('Key name is required');
      return;
    }
    
    if (newKeyName.trim() === keyName) {
      onClose();
      return;
    }
    
    try {
      await onSave(newKeyName.trim());
      onClose();
    } catch (err) {
      setError('Failed to update key name');
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting key:', err);
      setIsDeleting(false);
    }
  };

  const headerActions = (
    <div className="dialog-header-actions">
      <Button 
        variant="secondary" 
        onClick={onClose}
        disabled={isLoading || isDeleting}
        size="small"
      >
        Cancel
      </Button>
      <Button 
        variant="primary" 
        onClick={handleSubmit}
        disabled={isLoading || isDeleting}
        isLoading={isLoading}
        size="small"
      >
        Save
      </Button>
    </div>
  );

  const dialogActions = (
    <div className="dialog-actions-left">
      <DeleteButton
        onDelete={handleDelete}
        disabled={isLoading}
        size="small"
      />
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Translation Key"
      actions={dialogActions}
      headerActions={headerActions}
      showCloseButton={false}
    >
      <form onSubmit={handleSubmit} className="edit-key-form">
        <Input
          label="Key Name"
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          placeholder="Enter key name"
          error={error}
          disabled={isLoading || isDeleting}
          required
        />
      </form>
    </Dialog>
  );
};

export default EditKeyDialog; 